import React, { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  MapPinIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  MapIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  SignalIcon,
  SignalSlashIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  PlayIcon,
  PauseIcon,
  BoltIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline'
import { Button } from '@/components/ui/Button'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Alert } from '@/components/ui/Alert'
import { gpsApi, VehicleTracking, AlarmRecord, GPSDevice } from '@/api/gpsApi'
import { institutionsApi, type Institution } from '@/api/institutionsApi'
import { useAuth } from '@/hooks/useAuth'
import VehicleMapModal from '@/components/transport/VehicleMapModal'
import VehiclePlaybackModal from '@/components/transport/VehiclePlaybackModal'
import ExportModal from '@/components/ui/ExportModal'
import { type ExportConfig } from '@/utils/exportUtils'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

interface DashboardMetrics {
  totalVehicles: number
  activeVehicles: number
  offlineVehicles: number
  recentAlarms: number
  averageSpeed: number
  totalMileageToday: number
}

interface AlarmData {
  imei: string
  deviceName: string
  alarms: AlarmRecord[]
}

const GPSTrackingDashboard: React.FC = () => {
  const { user } = useAuth()
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [selectedVehicle, setSelectedVehicle] = useState<{ device: GPSDevice; tracking: VehicleTracking } | null>(null)
  const [playbackVehicle, setPlaybackVehicle] = useState<GPSDevice | null>(null)
  const [showExportModal, setShowExportModal] = useState(false)
  const [statusFilter, setStatusFilter] = useState<'all' | 'online' | 'offline'>('all')
  const [institutionFilter, setInstitutionFilter] = useState<string>('all')
  const [institutions, setInstitutions] = useState<Institution[]>([])
  const queryClient = useQueryClient()

  const isSuperAdmin = user?.role === 'SUPER_ADMIN'

  // Fetch institutions for super admin filter
  useQuery({
    queryKey: ['institutions-for-gps'],
    queryFn: async () => {
      const response = await institutionsApi.getInstitutions({ limit: 1000 })
      setInstitutions(response?.institutions || [])
      return response
    },
    enabled: isSuperAdmin,
  })

  // Get the institutionId to filter by
  const filterInstitutionId = isSuperAdmin && institutionFilter !== 'all' ? institutionFilter : undefined

  // Emergency command mutation
  const emergencyMutation = useMutation({
    mutationFn: ({ imei, command }: { imei: string; command: string }) =>
      gpsApi.sendCommand(imei, command),
    onSuccess: (response) => {
      toast.success(`Command executed: ${response.data.command}`)
      queryClient.invalidateQueries({ queryKey: ['live-tracking'] })
    },
    onError: () => {
      toast.error('Failed to execute command')
    },
  })

  // Fetch all GPS devices (filtered by institution)
  const {
    data: devicesResponse,
    isLoading: devicesLoading,
    error: devicesError,
  } = useQuery({
    queryKey: ['gps-devices', filterInstitutionId],
    queryFn: () => gpsApi.getDeviceList(filterInstitutionId),
    retry: 1,
  })

  // Extract devices from response (axios wraps in data, and our API also has data property)
  const devices = useMemo(() => {
    return devicesResponse?.data?.data?.devices || []
  }, [devicesResponse])

  // Fetch real-time tracking for all vehicles (filtered by institution)
  const {
    data: trackingResponse,
    isLoading: trackingLoading,
  } = useQuery({
    queryKey: ['live-tracking', devices.map((d: GPSDevice) => d.imei), filterInstitutionId],
    queryFn: async () => {
      if (devices.length === 0) {
        return { data: { success: true, data: { devices: [], totalDevices: 0 } } }
      }
      const imeis = devices.map((device: GPSDevice) => device.imei)
      return await gpsApi.getDeviceTracking(imeis, filterInstitutionId)
    },
    refetchInterval: autoRefresh ? 15000 : false,
    enabled: devices.length > 0,
    retry: 1,
  })

  // Extract tracking data (axios wraps in data, and our API also has data property)
  const trackingDevices = useMemo(() => {
    return trackingResponse?.data?.data?.devices || []
  }, [trackingResponse])

  // Fetch recent alarms for all vehicles
  const { data: alarmsData } = useQuery({
    queryKey: ['dashboard-alarms'],
    queryFn: async () => {
      if (devices.length === 0) return []

      const endtime = Math.floor(Date.now() / 1000)
      const begintime = endtime - 24 * 60 * 60 // Last 24 hours

      const alarmPromises = devices.map((device) =>
        gpsApi
          .getDeviceAlarms(device.imei, begintime, endtime)
          .then((response) => ({
            imei: device.imei,
            deviceName: device.deviceName,
            alarms: response?.data?.alarmRecords?.slice(0, 5) || [],
          }))
          .catch(() => ({
            imei: device.imei,
            deviceName: device.deviceName,
            alarms: [],
          }))
      )

      return Promise.all(alarmPromises)
    },
    enabled: devices.length > 0,
  })

  // Calculate dashboard metrics
  const metrics: DashboardMetrics = useMemo(() => {
    const totalVehicles = devices.length
    const activeVehicles = trackingDevices.filter(
      (vehicle: VehicleTracking) => vehicle?.status?.dataStatus === 'OK'
    ).length
    const offlineVehicles = totalVehicles - activeVehicles
    const recentAlarms = (alarmsData || []).reduce(
      (total: number, device: AlarmData) => total + (device?.alarms?.length || 0),
      0
    )

    const averageSpeed = trackingDevices.length
      ? Math.round(
          trackingDevices.reduce(
            (sum: number, v: VehicleTracking) => sum + (v?.location?.speed || 0),
            0
          ) / trackingDevices.length
        )
      : 0

    const totalMileageToday = trackingDevices.reduce(
      (sum: number, v: VehicleTracking) => sum + (v?.metrics?.todayMileage || 0),
      0
    )

    return {
      totalVehicles,
      activeVehicles,
      offlineVehicles,
      recentAlarms,
      averageSpeed,
      totalMileageToday: Math.round(totalMileageToday),
    }
  }, [trackingDevices, alarmsData, devices])

  // Filter devices based on status
  const filteredDevices = useMemo(() => {
    if (statusFilter === 'all') return devices

    return devices.filter((device) => {
      const tracking = trackingDevices.find((t: VehicleTracking) => t.imei === device.imei)
      if (statusFilter === 'online') {
        return tracking?.status?.dataStatus === 'OK'
      }
      return !tracking || tracking?.status?.dataStatus !== 'OK'
    })
  }, [devices, trackingDevices, statusFilter])

  const handleEmergencyCommand = (imei: string, command: string) => {
    emergencyMutation.mutate({ imei, command })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OK':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'OFFLINE':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'BLOCKED':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'EXPIRED':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getAlarmSeverityColor = (alarmType: number) => {
    if ([1].includes(alarmType)) return 'bg-red-500' // Critical
    if ([26, 27, 28, 29].includes(alarmType)) return 'bg-orange-500' // High
    if ([4, 5, 6].includes(alarmType)) return 'bg-yellow-500' // Medium
    return 'bg-blue-500' // Low
  }

  const getExportConfig = (): ExportConfig => {
    return {
      title: 'GPS Fleet Tracking Report',
      subtitle: 'Real-time vehicle monitoring and fleet status',
      filename: `gps-fleet-report-${format(new Date(), 'yyyy-MM-dd')}`,
      generatedAt: format(new Date(), 'PPpp'),
      summary: [
        { label: 'Total Vehicles', value: metrics.totalVehicles.toString() },
        { label: 'Active Vehicles', value: metrics.activeVehicles.toString() },
        { label: 'Offline Vehicles', value: metrics.offlineVehicles.toString() },
        { label: 'Recent Alarms (24h)', value: metrics.recentAlarms.toString() },
        { label: 'Average Speed', value: `${metrics.averageSpeed} km/h` },
        { label: 'Total Mileage Today', value: `${metrics.totalMileageToday} km` },
      ],
      sections: [
        {
          title: 'Fleet Status',
          columns: [
            { header: 'Device Name', key: 'deviceName' },
            { header: 'IMEI', key: 'imei' },
            { header: 'Status', key: 'status' },
            { header: 'Speed (km/h)', key: 'speed' },
            { header: 'ACC', key: 'acc' },
            { header: 'Battery', key: 'battery' },
            { header: 'Location', key: 'location' },
          ],
          data: devices.map((device) => {
            const tracking = trackingDevices.find((t: VehicleTracking) => t.imei === device.imei)
            return {
              deviceName: device.deviceName,
              imei: device.imei,
              status: tracking?.status?.dataStatus || 'NO_DATA',
              speed: tracking?.location?.speed || 0,
              acc: tracking?.status?.acc || 'UNKNOWN',
              battery: tracking?.status?.battery ?? 'N/A',
              location: tracking
                ? `${tracking.location.latitude.toFixed(6)}, ${tracking.location.longitude.toFixed(6)}`
                : 'N/A',
            }
          }),
        },
        {
          title: 'Recent Alarms',
          columns: [
            { header: 'Device', key: 'device' },
            { header: 'Alarm Type', key: 'alarmType' },
            { header: 'Description', key: 'description' },
            { header: 'Time', key: 'time' },
          ],
          data:
            alarmsData
              ?.flatMap((device: AlarmData) =>
                device.alarms.map((alarm) => ({
                  device: device.deviceName,
                  alarmType: alarm.alarmType,
                  description: alarm.alarmDescription,
                  time: format(new Date(alarm.timing.systemTimestamp * 1000), 'PPpp'),
                }))
              )
              .slice(0, 50) || [],
        },
      ],
    }
  }

  if (devicesLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (devicesError) {
    return (
      <div className="p-8">
        <Alert type="error" message={`Failed to load GPS devices: ${devicesError}`} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">GPS Fleet Tracking</h1>
          <p className="text-gray-600 mt-1">Real-time vehicle monitoring and fleet management</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              autoRefresh
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {autoRefresh ? (
              <>
                <PauseIcon className="h-4 w-4 mr-2" />
                Pause
              </>
            ) : (
              <>
                <PlayIcon className="h-4 w-4 mr-2" />
                Resume
              </>
            )}
          </button>
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['live-tracking'] })}
            disabled={trackingLoading}
            className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon className={`h-5 w-5 ${trackingLoading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowExportModal(true)}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Metrics Dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Vehicles</p>
              <p className="text-2xl font-bold text-gray-900">{metrics.totalVehicles}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <TruckIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-green-600">{metrics.activeVehicles}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <SignalIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Offline</p>
              <p className="text-2xl font-bold text-red-600">{metrics.offlineVehicles}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <SignalSlashIcon className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Alarms (24h)</p>
              <p className="text-2xl font-bold text-orange-600">{metrics.recentAlarms}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Speed</p>
              <p className="text-2xl font-bold text-purple-600">{metrics.averageSpeed} km/h</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <ChartBarIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Today's Miles</p>
              <p className="text-2xl font-bold text-indigo-600">{metrics.totalMileageToday}</p>
            </div>
            <div className="p-3 bg-indigo-100 rounded-lg">
              <MapIcon className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filter:</span>
          </div>

          {/* Institution Filter (Super Admin Only) */}
          {isSuperAdmin && (
            <div className="flex items-center gap-2">
              <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
              <select
                value={institutionFilter}
                onChange={(e) => setInstitutionFilter(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Institutions</option>
                {institutions.map((institution) => (
                  <option key={institution.id} value={institution.id}>
                    {institution.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                statusFilter === 'all'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All ({devices.length})
            </button>
            <button
              onClick={() => setStatusFilter('online')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                statusFilter === 'online'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Online ({metrics.activeVehicles})
            </button>
            <button
              onClick={() => setStatusFilter('offline')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                statusFilter === 'offline'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Offline ({metrics.offlineVehicles})
            </button>
          </div>
          {trackingLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <LoadingSpinner size="sm" />
              Refreshing...
            </div>
          )}
          <div className="ml-auto text-sm text-gray-500">
            Last updated: {format(new Date(), 'HH:mm:ss')}
          </div>
        </div>
      </div>

      {/* Vehicle Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Fleet Status</h2>
        </div>

        <div className="p-6">
          {filteredDevices.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredDevices.map((device) => {
                const trackingInfo = trackingDevices.find(
                  (tracking: VehicleTracking) => tracking?.imei === device?.imei
                )

                return (
                  <motion.div
                    key={device.imei}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="border border-gray-200 rounded-xl p-5 hover:border-primary-200 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="p-2 bg-gray-100 rounded-lg mr-3">
                          <TruckIcon className="w-6 h-6 text-gray-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {device.deviceName || 'Unknown Device'}
                          </h3>
                          <p className="text-sm text-gray-500">IMEI: {device.imei}</p>
                        </div>
                      </div>

                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium border ${
                          trackingInfo
                            ? getStatusColor(trackingInfo.status.dataStatus)
                            : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                        }`}
                      >
                        {trackingInfo ? trackingInfo.status.dataStatus : 'NO_DATA'}
                      </span>
                    </div>

                    {trackingInfo ? (
                      <>
                        {/* Quick Stats */}
                        <div className="grid grid-cols-4 gap-3 mb-4">
                          <div className="text-center p-3 bg-gray-50 rounded-lg">
                            <div className="font-bold text-gray-900">{trackingInfo.location.speed}</div>
                            <div className="text-xs text-gray-500">km/h</div>
                          </div>
                          <div className="text-center p-3 bg-gray-50 rounded-lg">
                            <div className="font-bold text-gray-900">
                              {trackingInfo.status.battery ?? 'N/A'}
                            </div>
                            <div className="text-xs text-gray-500">Battery</div>
                          </div>
                          <div className="text-center p-3 bg-gray-50 rounded-lg">
                            <div
                              className={`font-bold ${
                                trackingInfo.status.acc === 'ON' ? 'text-green-600' : 'text-gray-600'
                              }`}
                            >
                              {trackingInfo.status.acc}
                            </div>
                            <div className="text-xs text-gray-500">ACC</div>
                          </div>
                          <div className="text-center p-3 bg-gray-50 rounded-lg">
                            <div className="font-bold text-gray-900">{trackingInfo.status.fuel}</div>
                            <div className="text-xs text-gray-500">Fuel</div>
                          </div>
                        </div>

                        {/* GPS Location */}
                        <div className="bg-blue-50 rounded-lg p-4 mb-4">
                          <div className="flex items-center mb-2">
                            <MapPinIcon className="w-4 h-4 text-blue-600 mr-2" />
                            <span className="text-sm font-medium text-blue-900">GPS Location</span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-sm text-blue-800">
                            <div>Lat: {trackingInfo.location.latitude.toFixed(6)}</div>
                            <div>Lng: {trackingInfo.location.longitude.toFixed(6)}</div>
                            <div>Course: {trackingInfo.location.course}°</div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={() => handleEmergencyCommand(device.imei, 'RELAY,1')}
                            size="sm"
                            variant="danger"
                            disabled={emergencyMutation.isPending}
                          >
                            <BoltIcon className="h-4 w-4 mr-1" />
                            Relay On
                          </Button>
                          <Button
                            onClick={() => handleEmergencyCommand(device.imei, 'RELAY,0')}
                            size="sm"
                            variant="secondary"
                            disabled={emergencyMutation.isPending}
                          >
                            Relay Off
                          </Button>
                          <Button
                            onClick={() => setSelectedVehicle({ device, tracking: trackingInfo })}
                            size="sm"
                            variant="ghost"
                          >
                            <MapPinIcon className="h-4 w-4 mr-1" />
                            Map
                          </Button>
                          <Button onClick={() => setPlaybackVehicle(device)} size="sm" variant="ghost">
                            <PlayIcon className="h-4 w-4 mr-1" />
                            Playback
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <InformationCircleIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">No tracking data available</p>
                        <p className="text-xs">Device may be offline or not responding</p>
                      </div>
                    )}

                    {/* Recent Alarm Indicator */}
                    {alarmsData?.find((alarmDevice: AlarmData) => alarmDevice.imei === device.imei)
                      ?.alarms?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center text-orange-600">
                          <ExclamationTriangleIcon className="w-4 h-4 mr-2" />
                          <span className="text-sm font-medium">
                            {
                              alarmsData?.find(
                                (alarmDevice: AlarmData) => alarmDevice.imei === device.imei
                              )?.alarms?.length
                            }{' '}
                            recent alarms
                          </span>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )
              })}
            </div>
          ) : (
            <Alert
              type="info"
              message={
                statusFilter !== 'all'
                  ? `No ${statusFilter} vehicles found.`
                  : 'No GPS devices found. Check if vehicles have been registered with GPS devices.'
              }
            />
          )}
        </div>
      </div>

      {/* Recent Alarms Section */}
      {alarmsData && alarmsData.some((device: AlarmData) => device.alarms.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-orange-600 mr-2" />
                Critical Alarms (Last 24 Hours)
              </h2>
            </div>
          </div>

          <div className="p-6">
            <div className="space-y-4">
              {alarmsData
                .filter((device: AlarmData) => device.alarms.length > 0)
                .slice(0, 5)
                .map((device: AlarmData) => (
                  <div key={device.imei} className="border-l-4 border-orange-400 pl-4 py-2">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">{device.deviceName}</h3>
                      <span className="text-sm text-gray-500">IMEI: {device.imei}</span>
                    </div>

                    <div className="space-y-2">
                      {device.alarms.slice(0, 3).map((alarm, index) => (
                        <div key={index} className="flex items-center text-sm">
                          <div
                            className={`w-2 h-2 rounded-full mr-3 ${getAlarmSeverityColor(
                              alarm.alarmType
                            )}`}
                          ></div>
                          <span className="text-gray-700 flex-1">{alarm.alarmDescription}</span>
                          <span className="text-gray-500 text-xs">
                            {format(new Date(alarm.timing.systemTimestamp * 1000), 'MMM d, HH:mm')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Vehicle Map Modal */}
      {selectedVehicle && (
        <VehicleMapModal
          isOpen={!!selectedVehicle}
          onClose={() => setSelectedVehicle(null)}
          device={selectedVehicle.device}
          trackingData={selectedVehicle.tracking}
        />
      )}

      {/* Vehicle Playback Modal */}
      {playbackVehicle && (
        <VehiclePlaybackModal
          isOpen={!!playbackVehicle}
          onClose={() => setPlaybackVehicle(null)}
          device={playbackVehicle}
        />
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        config={getExportConfig()}
        title="Export GPS Fleet Report"
      />
    </div>
  )
}

export default GPSTrackingDashboard
